//Library imports
import React, { Fragment, useState, useEffect } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useLocation } from 'react-router-dom';

//Services
import { getStudentOptions } from '../../api/services/studentService';
import { searchAdminCaseloads } from '../../api/services/caseloadService';
import { getTherapistRoles, getTherapistsByDisciplineId } from '../../api/services/therapistRolesService';
import { getStudentStatuses } from '../../api/services/studentStatusService';
import { getDistrictOptions, getTherapistDistricts } from '../../api/services/districtService';
import { getDisciplines } from '../../api/services/disciplineService';
import { getSchoolsByDistrict } from '../../api/services/schoolService';

//Models and data types
import caseloadDisplay from '../../api/models/caseloadDisplay';
import { selectOption } from '../../types/selectOption';

//Components
import Layout from '../../components/layout';
import SubmitButton from '../../components/submitButton';
import DataTable from '../../components/dataTable';
import SelectMulti from 'react-select';
import { Notification } from '../../components/notifications';
import { getDisciplinesByTherapistId, getTherapistNamesAndIds } from '../../api/services/therapistService';
import moment from 'moment';
import Therapists from '../../api/models/therapists';
import User from '../../api/models/user';
import AutoCompleteOption from '../../api/models/autoCompleteModel';

//Utility functions
import { getGrades } from '../../api/services/gradeService';
import FetchInput from '../../components/FetchInput';
import DistrictsResponse from '../../api/responses/districtsResponse';
import { getCurrentUser } from '../../api/services/meService';
import { generateSelectOptions } from '../../common/utils';
import { RoleIdLookup } from '../../common/constants';
import { DataResponse } from '../../api/responses/baseResponse';
import { getProgramOptions } from '../../api/services/programService';
import CustomAlert from '../../components/alert/commonAlert';

//Get our view state
type ViewState = {
    message?: string;
    studentDuplicate?: boolean;
    ids?: [];
};

/**
 * Methods to fetch search criteria with which to populate
 */
/*const fetchStudents = async (search: string) => {
    return await getStudentOptions(search);
};*/

const fetchTherapists = async () => {
    return await getTherapistNamesAndIds();
};

const fetchStudentStatuses = async () => {
    return getStudentStatuses();
};

const fetchDisciplines = async () => {
    return getDisciplines();
};

const fetchTherapistRoles = async () => {
    return getTherapistRoles();
};

const fetchStudents = async (args: any[]) => {
    return getStudentOptions(args[0])
};

const fetchPrograms = async () => {
    return getProgramOptions();
}

/**
 * Primary Component
 */
const CaseloadSearch = () => {
    //Process our DataTable columns and data
    const columns = React.useMemo(
        () => [
            {
                Header: "Student Id",
                accessor: (s: any) => s.studentId,
            },
            {
                Header: 'Student',
                accessor: (s: any) => `${s.studentLastName}, ${s.studentFirstName}`,
            },
            {
                Header: 'Discipline',
                accessor: (s: any) => s.schoolId < 1 || !s.isActive ? '' : s.disciplineName
            },
            {
                Header: 'District',
                accessor: (s: any) => s.districtName,
                width: 250,
            },
            {
                Header: 'Program',
                accessor: (s: any) => s.programName,
            },
            {
                Header: 'School',
                accessor: (s: any) => s.schoolName,
            },
            {
                Header: 'Status',
                accessor: (s: any) => !s.isActive || s.schoolId < 1 ? '' : s.studentStatus,
            },
            {
                Header: 'Grade',
                accessor: (s: any) => s.gradeName,
            },
            {
                Header: 'IEP',
                accessor: (s: any) => (s.iepStartDate === null || s.iepEndDate === null) || !s.isActive || s.schoolId < 1 ? '' : `${moment(s.iepStartDate).format('l')} - ${moment(s.iepEndDate).format('l')}`,
            },
            {
                Header: 'Therapist Type',
                accessor: (s: any) => !s.therapyType || s.therapyType === '' || !s.isActive || s.schoolId < 1 ? '' : s.therapyType,
            },
            {
                Header: 'Primary Therapist',
                accessor: (s: any) => !s.isActive || s.schoolId < 1 || s.studentStatus === 'Not On Caseload' ? '' : `${s.therapistLastName}, ${s.therapistFirstName}`,
            },
            {
                accessor: 'studentId',
                className: 'hidden',
            },
            {
                accessor: 'caseloadId',
                className: 'hidden',
            },
        ],
        //Disabling next line for eslint only because we need an empty dependency array so the effec tis only called once, on load
        // eslint-disable-next-line
        []
    );

    //App hooks
    const { state } = useLocation<ViewState>();
    const history = useHistory();

    //Select element options
    const [therapists, setTherapists] = useState<selectOption[]>([]);
    const [therapistOptions, setTherapistOptions] = useState<AutoCompleteOption[]>([{ id: -1, name: "enter text to search" }]);
    const [schoolOptions, setSchoolOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [statusOptions, setStatusOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [districtOptions, setDistrictOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [disciplineOptions, setDisciplineOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [therapistRoleOptions, setTherapistTypeOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [gradeOptions, setGradeOptions] = useState<selectOption[]>([]);
    const [currentUser, setCurrentUser] = useState<User>();
    const [programOptions, setProgramOptions] = useState<selectOption[]>([]);

    //Data hooks
    const [therapist, setTherapist] = useState<number>(-1);
    const [school, setSchool] = useState<number>(-1);
    const [status, setStatus] = useState<number>(-1);
    const [district, setDistrict] = useState<number>(-1);
    const [disciplineId, setDisciplineId] = useState<number>(-1);
    const [name, setName] = useState('');
    const [caseloads, setCaseloads] = useState<caseloadDisplay[]>([]);
    const [grade, setGrade] = useState<number>(-1);
    const [therapistRole, setTherapistType] = useState<number>(-1);
    const [therapistName, setTherapistName] = useState<string>('');
    const [programId, setProgramId] = useState<number>(-1);

    const [disciplineName, setDisciplineName] = useState<string>('');
    const [districtName, setDistrictName] = useState<string>('');
    const [schoolName, setSchoolName] = useState<string>('');

    /**
     * Options lists (for our select elements)
     */
    const getStudentsAutoCompleteAsync = useAsync({
        deferFn: fetchStudents,
        onResolve: (u) => {
            let options: AutoCompleteOption[] = [];

            //set options           
            (u.data.map((g) => (
                options.push({ id: g.studentId ?? -1, name: g.fullName })
            )));
            options.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });

            //setStudentOptions([...options]);
            return new Promise<AutoCompleteOption[]>((resolve) => resolve(options));
        }
    });

    const getTherapistsAutoCompleteAsync = useAsync({
        promiseFn: fetchTherapists,
        onResolve: (u) => setTherapists(u.data)
    });

    const studentStatusesData = useAsync({
        promiseFn: fetchStudentStatuses,
        onResolve: (u) => {
            let options: selectOption[] = statusOptions;

            //set options           
            (u.data.studentStatuses.map((g) => (
                options.push({ value: g.studentStatusId ?? -1, label: g.name })
            )));
            options.sort((a, b) => {
                return a.label.localeCompare(b.label);
            });
            setStatusOptions(options);
        },
    });

    /**
     * If the user is a therapist only, get only the
     * districts in which that therapist has an active billing rate
     */
    useEffect(() => {
        if (!currentUser) {
            //Get current user
            const fetchUser = async () => {
                const userResponse = await getCurrentUser();

                //Get the user information
                const user: User = userResponse.data;

                //Set the current user hook
                setCurrentUser(user);

                //Get the therapists districts
                if (user.userRoles.filter(x =>
                    x.roleId === RoleIdLookup.Admin ||
                    x.roleId === RoleIdLookup.BillingSpecialist ||
                    x.roleId === RoleIdLookup.Director ||
                    x.roleId === RoleIdLookup.Client
                )) {
                    await getDistrictOptions().then(therapistDistrictsResolve);
                    //await getDistricts().then(districtsResolve);
                } else {
                    await getTherapistDistricts(1, new Date().getFullYear() - 1, user.userId).then(districtsResolve);
                }
            }

            fetchUser();
        }
    }, []);

    const therapistDistrictsResolve = async (districts: DataResponse<selectOption[]>) => {
        setDistrictOptions(districts.data);
    }

    const districtsResolve = async (districts: DistrictsResponse) => {
        const options = generateSelectOptions(districts.data.districts, 'districtId', 'name');
        setDistrictOptions(options);
    }

    const disciplinesData = useAsync({
        promiseFn: fetchDisciplines,
        onResolve: (u) => {
            const options = generateSelectOptions(u.data.disciplines, 'disciplineId', 'name', true);
            setDisciplineOptions(options);
        },
    });

    const gradesData = useAsync({
        promiseFn: getGrades,
        onResolve: (u) => {
            const options = generateSelectOptions(u.data.grades, 'gradeId', 'name');
            setGradeOptions(options);
        },
    });

    const programAsync = useAsync({
        promiseFn: fetchPrograms,
        onResolve: (u) => {
            let options: selectOption[] = programOptions;
            u.data.map((g) => options.push({ value: g.value ?? - 1, label: g.label }));
            options.shift();
            setProgramOptions(options);
        }
    })

    const therapistRolesData = useAsync({
        promiseFn: fetchTherapistRoles,
        onResolve: (u) => {
            const options = generateSelectOptions(u.data.therapistRoles, 'therapistRoleId', 'name', true);
            setTherapistTypeOptions(options);
        },
    });

    /**
     * Search Handling Logic
     */
    const handleSearch = async () => {
        if (Number.isNaN(therapist)) setTherapist(-1);
        const therapistId = (Number.isNaN(therapist) ? -1 : therapist);

        try {
            const response = await searchAdminCaseloads(name, therapistId, status, district, school, programId, disciplineId, grade, therapistRole, therapistName, disciplineName, districtName, schoolName);

            //Loop through results to process
            let data: caseloadDisplay[] = response.data.caseloads;
            let ids: number[] = [];

            data.map((caseload) => ids.push(caseload.caseloadId));

            setCaseloads(data);
        } catch (error: any) {
            console.error(error);
        }

        return new Promise<void>((resolve) => resolve());
    };
    const handleSearchAsync = useAsync({ deferFn: handleSearch });

    /**
     * Whenever therapist changes, update the list of disciplines
     */
    useEffect(() => {
        updateDisciplines(therapist);
    }, [therapist]);

    /**
     * This effect updates the therapists every time the disciplineId is chanegd
     */
    useEffect(() => {
        if (disciplineId) {
            const updateTherapists = async () => {
                const response = await getTherapistsByDisciplineId(disciplineId);
                const therapists: Therapists[] = response.data.therapists;

                if (therapists.length > 0) {
                    let options: AutoCompleteOption[] = [];
                    //set options           
                    therapists.map((t: Therapists) => {
                        if (t?.disciplineId === disciplineId) {
                            options.push({ id: t.userId ?? -1, name: t.user?.lastName + ", " + t.user?.firstName });
                        }
                        return true;
                    });

                    setTherapistOptions([...options]);
                }
            }

            //Calling the above async so we only set the caseloads after they are all returned
            updateTherapists();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disciplineId]);

    /**
     * Whenever district changes, update the list of schools
     */
    useEffect(() => {
        if (district > 0) updateSchools(district);
    }, [district, districtName]);

    /**
     * Functions to update the select boxes
     */
    const updateSchools = async (district: number) => {
        const response = await getSchoolsByDistrict(district);

        //If we don't have any schools, inform the user
        if (response.data.schools.length === 0) {
            setSchoolOptions([{ value: -1, label: '' }]);
            return true;
        }

        const schools = generateSelectOptions(response.data.schools, 'schoolId', 'name', true);
        setSchoolOptions(schools);
    }

    const updateDisciplines = async (discipline: number) => {
        const response = (discipline > 0) ? await getDisciplinesByTherapistId(therapist) : await getDisciplines();
        const disciplines = generateSelectOptions(response.data.disciplines, 'disciplineId', 'name', true);
        setDisciplineOptions(disciplines);
    }
    const handleCloseAlert = () => {
        history.replace({
            pathname: window.location.pathname,
            state: {
              ...state, // Keep other properties
              studentDuplicate: null, // Set userId to null
            },
          });
    };
    /**
     * To avoid the results being applied before they are receiveed,
     * we are going to set an effect on the name variable, which is
     * changed by the autocomplete field. This will fire our async
     * which will now wait for the results first
     */
    /*useEffect(() => {
        getStudentsAutoCompleteAsync.run(name);
    }, [name]);*/

    return (
        <Layout isLoading={
            handleSearchAsync.isPending ||
            getTherapistsAutoCompleteAsync.isPending ||
            //districtsData.isPending ||
            studentStatusesData.isPending ||
            disciplinesData.isPending ||
            handleSearchAsync.isPending ||
            therapistRolesData.isPending ||
            gradesData.isPending ||
            programAsync.isPending
        }>
            <Fragment>
                <Fragment>
                    {state?.message && <Notification>{state?.message}</Notification>}
                    {state?.studentDuplicate &&  (<CustomAlert
                            message={"This student's name and DoB now match those of an existing student.  Please use the Student Merge page to consolidate the duplicate students."}
                            onClose={handleCloseAlert}
                    />)}
                    {/* Select Boxes */}
                    <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                        <div className='z-0 grid grid-cols-6 gap-6'>
                            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
                                <FetchInput
                                    value={name}
                                    rounded='sm'
                                    placeholder=''
                                    label={`Student Name`}
                                    fetchProperty={`fullNameLtr`}
                                    altFetchProperty={`fullName`}
                                    valueProperty={`fullNameLtr`}
                                    //fetchFunction={fetchStudents}
                                    onClear={() => setName('')}
                                    onChange={(value: string) => setName(value)}
                                    icon={'search'}
                                />
                            </div>

                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <FetchInput
                                    value={therapistName}
                                    rounded='sm'
                                    placeholder=''
                                    label={`Therapist`}
                                    key={"therapist_ac"}
                                    fetchProperty={`fullNameLtr`}
                                    altFetchProperty={`fullName`}
                                    valueProperty={`fullNameLtr`}
                                    fetchData={therapists}
                                    onClear={() => setTherapistName('')}
                                    onChange={(value: string) => setTherapistName(value)}
                                    icon={'search'}
                                />
                            </div>

                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Status</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={statusOptions}
                                    isClearable={true}
                                    onChange={(e: any | null) => {
                                        //Null exception handling
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setStatus(Number(e.value))
                                    }}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <FetchInput
                                    value={districtOptions.find((x) => x.value === district)?.label ?? ''}
                                    rounded='sm'
                                    placeholder=''
                                    label={`Districts`}
                                    fetchProperty={`label`}
                                    valueProperty={`value`}
                                    fetchData={districtOptions}
                                    onChange={(value: string) => {
                                        setDistrictName(value);
                                        //setDistrict(value);
                                    }}
                                    onClear={() => {
                                        setDistrictName('');
                                        //  setDistrict(-1);
                                    }}
                                    icon={'search'}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <FetchInput
                                    value={schoolOptions.find((x) => x.value === school)?.label ?? ''}
                                    rounded='sm'
                                    placeholder=''
                                    label={`School`}
                                    fetchProperty={`label`}
                                    valueProperty={`value`}
                                    returnRawValue={true}
                                    fetchData={schoolOptions}
                                    onChange={(value: string | number) => setSchoolName(value.toString())}
                                    onClear={() => setSchoolName('')}
                                    icon={'search'}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Disicpline</label>
                                <select
                                    className='block primary'
                                    value={disciplineName}
                                    onChange={(e) => setDisciplineName(e.currentTarget.value)}
                                    placeholder='Select...'
                                >
                                    <option value=""></option>
                                    {disciplineOptions.sort((a, b) => {
                                        return a.label.localeCompare(b.label);
                                    }).map((item, idx) => <option key={`pageSize_${item.label}`}>{item.label}</option>)}
                                </select>
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Grade</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={gradeOptions}
                                    isClearable={true}
                                    onChange={(e: any | null) => {
                                        //Null exception handling
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setGrade(Number(e.value));
                                    }}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Therapist Type</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={therapistRoleOptions}
                                    isClearable={true}
                                    onChange={(e: any | null) => {
                                        //Null exception handling
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setTherapistType(Number(e.value));
                                    }}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Program</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={programOptions}
                                    isClearable={true}
                                    onChange={(e: any) => {
                                        //Null exception handling
                                        e = e ?? { label: '', value: "-1" };
                                        setProgramId(e.value);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Button Row */}
                    <div className='flex px-4 py-3 text-right bg-gray-50 sm:px-6'>
                        <SubmitButton
                            text='Search'
                            className='mr-5 bg-ptsblue-normal hover:bg-ptsblue-dark'
                            onClick={() => {
                                handleSearchAsync.run();
                            }}
                        />
                    </div>

                    {/* Caseloads Display Table */}
                    {caseloads.length > 0 && (
                        <div className='overflow-hidden border-b border-gray-200 rounded-lg shadow'>
                            <DataTable
                                columns={columns}
                                data={caseloads}
                                showPagination={true}
                                getRowProps={(row: any) => ({
                                    onClick: () => {
                                        if (row.original.caseloadId) {
                                            history.push(`/caseload/${row.original.caseloadId}`, { referrer: 'searchCaseloads', idSpecific: row.original.isActive ? 'caseload' : 'student' })
                                        }
                                    }
                                })}
                            />
                        </div>
                    )}

                    {/* Students Not Found Message */}
                    {caseloads.length === 0 && (
                        <div>
                            <label>No students found.</label>
                            <div className='px-4 py-3 bg-gray-100 sm:px-6 sm:flex sm:flex-row-reverse'>

                            </div>
                        </div>
                    )}
                </Fragment>
            </Fragment>
        </Layout>

    );
};

export default CaseloadSearch;
